import React, { createContext, useEffect, useState, useCallback, useRef, useContext } from 'react'
import { v4 as uuidv4 } from 'uuid'
import _set from 'lodash.set'
import _cloneDeep from 'lodash.clonedeep'
import { MAX_CONDITION_GROUP_DEPTH } from '../../../common/constants'
import ReactGA from 'react-ga4'
import { Pattern, PatternContext } from './PatternContext'
import getConditionLabel from '../../../common/utils/getConditionLabel'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { useSavePattern } from '../../../hooks/useSavePattern'
import { captureEvent } from '../../../posthog'
import { getConditionTypes } from '../condition/conditions'
import { normalizeConditionValidity } from '../utils/normalizeConditionValidity'
import { migrateLegacyChangedConditions } from '../utils/migrateLegacyChangedConditions'
import useIndicatorRHS from '../hooks/useIndicatorRHS'

export type CandleTarget = {
  offset: number // 0 = current candle, 1 = 1st previous, N = Nth previous
  lookback?: {
    count: number // Number of previous candles to compare against
    mode: 'all' | 'some' // How many must satisfy the condition
    needed?: number // For 'some' mode: at least N must pass
  }
}

export type ConditionType = {
  id: string
  type: 'condition'
  chartDetails: {
    symbol: string
    periodicity: number
    timeframe: string
  }
  condition: {
    LHS?: {
      field: string | 'value'
      value?: any
      indicator: { name: string; params?: { [key: string]: any } }
      candle?: CandleTarget
    }
    conditionType: string | undefined
    RHS?: {
      field: string | 'value'
      value?: any
      indicator: { name: string; params?: { [key: string]: any } }
      candle?: CandleTarget
    }
    threshold?: number
    isValid?: boolean
  }
  settings?: {
    previousCandle?: {
      enabled: boolean
      numberOfCandles: number
      candlesNeeded: number
    }
    validityWindow?: {
      candles: number
    }
    prerequisites?: {
      type: 'condition' | 'group'
      id: string
    }[]
  }
  sourceGroupId: string
}

export type ConditionGroupType = {
  id: string
  sourceGroupId?: string
  groupTitle: string
  type: 'group'
  conditions: ConditionType[]
  conditionGroups: ConditionGroupType[]
  depth: number
  dropIndex?: number
  count: number

  symbol: string
  periodicity: number
  timeframe: string
}

// Re-exported for existing importers; implementation lives in utils so pure
// consumers don't drag this provider's import tree.
export { migrateLegacyChangedConditions }

export function PatternTreeProvider({
  children,
  pattern: initialPattern,
  isTradingHousePattern = false,
}: any) {
  const [pattern, setPattern] = useState<Pattern>(() =>
    migrateLegacyChangedConditions(initialPattern)
  )
  const [activeTab, setActiveTab] = useState<'buy' | 'sell'>('buy')
  // Add a ref to track the latest pattern value
  const latestPatternRef = useRef<Pattern>(initialPattern)
  // Track newly created condition IDs so they can auto-open in edit mode
  const newlyCreatedConditionIds = useRef<Set<string>>(new Set())

  const { getIndicatorByValue } = useContext(PatternContext)

  // Update the ref whenever pattern changes
  useEffect(() => {
    latestPatternRef.current = pattern
  }, [pattern])

  const { usermeta, tradingHouse, canViewPrivatePatterns } = useApplicationSuiteContext()

  const { getRHS } = useIndicatorRHS()

  useEffect(() => {
    const migrated = migrateLegacyChangedConditions(initialPattern)
    setPattern(migrated)
    latestPatternRef.current = migrated
  }, [initialPattern])

  // Get save pattern functions from our new hook
  const { forceSave, isSavingPattern } = useSavePattern(
    setPattern,
    usermeta,
    tradingHouse,
    canViewPrivatePatterns
  )

  // Helper function to find a group and its parent group
  const findGroupAndParent = (
    groups: ConditionGroupType[],
    groupId: string,
    parentGroup?: ConditionGroupType
  ): { group: ConditionGroupType; parentGroup?: ConditionGroupType } | null => {
    for (const group of groups) {
      if (group.id === groupId) {
        return { group, parentGroup }
      }

      const result = findGroupAndParent(group.conditionGroups, groupId, group)
      if (result) {
        return result
      }
    }

    return null
  }

  const updateGroupInTree = (
    groups: ConditionGroupType[],
    groupId: string,
    updateFn: (group: ConditionGroupType) => ConditionGroupType
  ): ConditionGroupType[] => {
    return groups.map((group) => {
      if (group.id === groupId) {
        // Apply the update function to the group that matches the id
        return updateFn(_cloneDeep(group))
      } else if (group.conditionGroups.length > 0) {
        // Recursively update conditionGroups
        return {
          ...group,
          conditionGroups: updateGroupInTree(group.conditionGroups, groupId, updateFn),
        }
      } else {
        // Return the group unchanged if it's not the one we're looking for
        return group
      }
    })
  }

  const findConditionInGroup = (
    groups: ConditionGroupType[],
    conditionId: string
  ): ConditionType | null => {
    for (const group of groups) {
      // Check if the condition is in the current group's conditions
      const condition = group.conditions.find((c) => c.id === conditionId)
      if (condition) {
        return condition
      }

      // Recursively search in the group's conditionGroups
      const result = findConditionInGroup(group.conditionGroups, conditionId)
      if (result) {
        return result
      }
    }

    return null
  }

  const updateConditionInGroup = (
    groups: ConditionGroupType[],
    conditionId: string,
    updateFn: (condition: ConditionType) => ConditionType
  ): ConditionGroupType[] => {
    return groups.map((group) => {
      // Check if the condition is in the current group's conditions
      const updatedConditions = group.conditions.map((condition) => {
        if (condition.id === conditionId) {
          // Apply the update function to the condition that matches the id
          return updateFn(_cloneDeep(condition))
        } else {
          return condition
        }
      })

      // Recursively update conditionGroups
      const updatedConditionGroups = updateConditionInGroup(
        group.conditionGroups,
        conditionId,
        updateFn
      )

      // Return the updated group
      return {
        ...group,
        conditions: updatedConditions,
        conditionGroups: updatedConditionGroups,
      }
    })
  }

  // Helper function to find a condition group in a nested tree structure
  const findConditionGroupInTree = (groups: ConditionGroupType[], groupId: string): boolean => {
    for (const group of groups) {
      if (group.id === groupId) {
        return true
      }

      if (
        group.conditionGroups.length > 0 &&
        findConditionGroupInTree(group.conditionGroups, groupId)
      ) {
        return true
      }
    }

    return false
  }

  // Helper function to find the maximum depth of nested groups
  const findMaxDepth = (obj: ConditionGroupType, currentDepth = 1) => {
    // If there's no "groups" field or it's empty, return current depth.
    if (!obj.conditionGroups || obj.conditionGroups.length === 0) {
      return currentDepth
    }

    // Otherwise, find the maximum depth of all child nodes and return it.
    let maxChildDepth = currentDepth
    for (const group of obj.conditionGroups) {
      const childDepth = findMaxDepth(group, currentDepth + 1)
      if (childDepth > maxChildDepth) {
        maxChildDepth = childDepth
      }
    }

    return maxChildDepth
  }

  // Helper function to remove a group and its nested groups recursively
  const removeGroupAndChildren = (
    groups: ConditionGroupType[],
    groupId: string
  ): ConditionGroupType[] => {
    return groups.reduce((acc: ConditionGroupType[], group) => {
      if (group.id !== groupId) {
        const updatedGroup = {
          ...group,
          conditionGroups: removeGroupAndChildren(group.conditionGroups, groupId),
        }
        acc.push(updatedGroup)
      }
      return acc
    }, [])
  }

  // Collect all condition and group IDs contained within the specified group
  const collectIdsInGroup = (groups: ConditionGroupType[], groupId: string): Set<string> => {
    const ids = new Set<string>()

    const walkGroup = (group: ConditionGroupType) => {
      ids.add(group.id)
      for (const condition of group.conditions) {
        ids.add(condition.id)
      }
      for (const subGroup of group.conditionGroups) {
        walkGroup(subGroup)
      }
    }

    const findGroup = (groups: ConditionGroupType[]) => {
      for (const group of groups) {
        if (group.id === groupId) {
          walkGroup(group)
          return
        }
        findGroup(group.conditionGroups)
      }
    }

    findGroup(groups)
    return ids
  }

  // Remove prerequisite entries referencing any of the given IDs from all conditions in the tree
  const stripPrerequisiteRefs = (
    groups: ConditionGroupType[],
    removedIds: Set<string>
  ): ConditionGroupType[] => {
    return groups.map((group) => ({
      ...group,
      conditions: group.conditions.map((condition) => {
        if (!condition.settings?.prerequisites?.length) return condition
        const filtered = condition.settings.prerequisites.filter((p) => !removedIds.has(p.id))
        return {
          ...condition,
          settings: {
            ...condition.settings,
            prerequisites: filtered.length > 0 ? filtered : undefined,
          },
        }
      }),
      conditionGroups: stripPrerequisiteRefs(group.conditionGroups, removedIds),
    }))
  }

  const recalculateGroupHierarchy = (
    group: ConditionGroupType,
    parentDepth: number = 0,
    regenerateIds: boolean = true
  ) => {
    // Build ID mapping for prerequisite updates
    const oldToNewIdMap = new Map<string, string>()
    const oldToNewGroupIdMap = new Map<string, string>()

    // First pass: regenerate IDs and build mapping
    const regenerateIdsInGroup = (grp: ConditionGroupType) => {
      if (regenerateIds) {
        const oldGroupId = grp.id
        const newGroupId = uuidv4()
        grp.id = newGroupId
        oldToNewGroupIdMap.set(oldGroupId, newGroupId)
      }

      // Update all conditions within this group
      for (const condition of grp.conditions) {
        if (regenerateIds) {
          const oldConditionId = condition.id
          const newConditionId = uuidv4()
          condition.id = newConditionId
          oldToNewIdMap.set(oldConditionId, newConditionId)
        }
        condition.sourceGroupId = grp.id
      }

      // Recursively update all nested condition groups
      for (const nestedGroup of grp.conditionGroups) {
        nestedGroup.sourceGroupId = grp.id
        regenerateIdsInGroup(nestedGroup)
      }
    }

    // Second pass: update prerequisite references
    const updatePrerequisites = (grp: ConditionGroupType) => {
      for (const condition of grp.conditions) {
        if (condition.settings?.prerequisites) {
          condition.settings.prerequisites = condition.settings.prerequisites.map((prereq: any) => {
            if (prereq.type === 'condition' && oldToNewIdMap.has(prereq.id)) {
              return { ...prereq, id: oldToNewIdMap.get(prereq.id) }
            } else if (prereq.type === 'group' && oldToNewGroupIdMap.has(prereq.id)) {
              return { ...prereq, id: oldToNewGroupIdMap.get(prereq.id) }
            }
            return prereq
          })
        }
      }

      for (const nestedGroup of grp.conditionGroups) {
        updatePrerequisites(nestedGroup)
      }
    }

    // Execute first pass
    regenerateIdsInGroup(group)

    // Execute second pass if we regenerated IDs
    if (regenerateIds) {
      updatePrerequisites(group)
    }

    // Set the appropriate depth and sourceGroupId based on parent
    if (parentDepth === 0) {
      group.depth = 1 // Root level
      group.sourceGroupId = undefined // Clear sourceGroupId for root level groups
    } else {
      group.depth = parentDepth + 1
      // Note: sourceGroupId should be set by the caller when moving/adding groups
    }

    // Update depths recursively
    const updateDepths = (grp: ConditionGroupType, depth: number) => {
      grp.depth = depth
      for (const nestedGroup of grp.conditionGroups) {
        updateDepths(nestedGroup, depth + 1)
      }
    }

    updateDepths(group, parentDepth === 0 ? 1 : parentDepth + 1)
  }

  function updateConditionGroupCount(group: ConditionGroupType, delta: number = 0) {
    const groupMaxCount = (group.conditionGroups?.length ?? 0) + (group.conditions?.length ?? 0)
    if (group.count === groupMaxCount) {
      group.count += delta
    }
  }

  // Function for manual save that can be exposed to UI
  const savePattern = useCallback(
    async (saveAsStandardPattern: boolean = false) => {
      // Always use the most up-to-date pattern from the ref
      const currentPattern = latestPatternRef.current
      if (!currentPattern) return
      const savedPattern = await forceSave(currentPattern, saveAsStandardPattern)
      if (savedPattern) {
        captureEvent('pattern_saved', {
          save_target: saveAsStandardPattern ? 'standard' : 'personal',
        })
      }
      return savedPattern
    },
    [forceSave]
  )

  const addConditionGroup = (conditionGroup?: ConditionGroupType) => {
    ReactGA.event('condition_group_add_to_root', {
      source: conditionGroup ? 'saved' : 'new',
    })

    const newGroupId = uuidv4()

    // When creating a brand new group (not from a saved template), seed it with an empty condition
    const seedCondition: ConditionType | null = !conditionGroup
      ? {
          id: uuidv4(),
          type: 'condition',
          chartDetails: { symbol: 'EURUSD', periodicity: 1, timeframe: 'minute' },
          condition: { LHS: undefined, conditionType: '', RHS: undefined, isValid: false },
          sourceGroupId: newGroupId,
        }
      : null

    const newGroup: ConditionGroupType = {
      type: 'group',
      groupTitle: ``,
      conditions: seedCondition ? [seedCondition] : [],
      conditionGroups: [],
      count: seedCondition ? 1 : 0,
      symbol: 'EURUSD',
      periodicity: 1,
      timeframe: 'minute',
      ...conditionGroup,
      id: newGroupId,
      depth: 1, // Will be set by recalculateGroupHierarchy
      sourceGroupId: undefined,
    }

    if (seedCondition) {
      newlyCreatedConditionIds.current.add(seedCondition.id)
    }

    recalculateGroupHierarchy(newGroup)

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]
      return _set(updatedPattern, activeConditionGroupKey, activeConditionGroup.concat(newGroup))
    })
  }

  const handleAddNestedGroup = (groupId: string, conditionGroup?: ConditionGroupType) => {
    ReactGA.event('condition_group_add_to_group', {
      source: conditionGroup ? 'saved' : 'new',
    })

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]

      // Find the parent group to check its depth
      const { group: parentGroup } = findGroupAndParent(activeConditionGroup, groupId) || {}
      if (!parentGroup) return updatedPattern

      // Check if adding a new nested group would exceed the maximum depth
      if (parentGroup.depth >= MAX_CONDITION_GROUP_DEPTH) {
        console.warn('Cannot add nested group: maximum depth reached')
        return updatedPattern
      }

      const updatedGroup = updateGroupInTree(
        activeConditionGroup,
        groupId,
        (group: ConditionGroupType) => {
          const newGroup: ConditionGroupType = {
            groupTitle: ``,
            type: 'group',
            symbol: group.symbol,
            periodicity: group.periodicity,
            timeframe: group.timeframe,
            conditions: [],
            conditionGroups: [],
            count: 0,
            ...conditionGroup,
            id: uuidv4(),
            sourceGroupId: groupId,
            depth: 0, // Temporary value, will be properly set by recalculateGroupHierarchy
          }

          // Set up proper hierarchy with parent
          recalculateGroupHierarchy(newGroup, group.depth)
          updateConditionGroupCount(group, 1)

          return {
            ...group,
            conditionGroups: group.conditionGroups.concat(newGroup),
          }
        }
      )

      return _set(updatedPattern, activeConditionGroupKey, updatedGroup)
    })
  }

  const handleRemoveConditionGroup = (groupId: string) => {
    ReactGA.event('condition_group_remove')

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]

      // Collect all condition/group IDs being removed before we remove them
      const removedIds = collectIdsInGroup(activeConditionGroup, groupId)

      const { parentGroup } = findGroupAndParent(activeConditionGroup, groupId)!

      const updatedGroups = removeGroupAndChildren(activeConditionGroup, groupId)

      if (parentGroup) {
        updateConditionGroupCount(parentGroup, -1)
        const { group: updatedParentGroup } = findGroupAndParent(updatedGroups, parentGroup.id)!

        const groupMaxCount =
          (updatedParentGroup.conditionGroups?.length ?? 0) +
          (updatedParentGroup.conditions?.length ?? 0)
        if (updatedParentGroup.count >= groupMaxCount) {
          updatedParentGroup.count = groupMaxCount
        }
      }

      // Strip any prerequisite references to the removed group/conditions
      const cleanedGroups = stripPrerequisiteRefs(updatedGroups, removedIds)

      return _set(updatedPattern, activeConditionGroupKey, cleanedGroups)
    })
  }

  const handleRemoveCondition = (sourceGroupId: string, conditionId: string) => {
    ReactGA.event('condition_remove')

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]

      const updatedGroup = updateGroupInTree(
        activeConditionGroup,
        sourceGroupId,
        (group: ConditionGroupType) => {
          const updatedConditions = group.conditions.filter(
            (condition: ConditionType) => condition.id !== conditionId
          )

          updateConditionGroupCount(group, -1)

          return {
            ...group,
            conditions: updatedConditions,
          }
        }
      )

      // Strip any prerequisite references to the deleted condition
      const cleanedGroups = stripPrerequisiteRefs(updatedGroup, new Set([conditionId]))

      return _set(updatedPattern, activeConditionGroupKey, cleanedGroups)
    })
  }

  const handleAddCondition = (groupId: string, condition?: ConditionType, index?: number) => {
    let conditionIndicatorLabel = ''
    if (condition) {
      conditionIndicatorLabel = getConditionLabel(condition!)
    }

    ReactGA.event('condition_add_to_group', {
      condition: conditionIndicatorLabel,
    })

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]

      const updatedGroup = updateGroupInTree(
        activeConditionGroup,
        groupId,
        (group: ConditionGroupType) => {
          const newCondition: ConditionType = {
            type: 'condition',
            chartDetails: {
              symbol: group.symbol,
              periodicity: group.periodicity,
              timeframe: group.timeframe,
            },
            condition: {
              LHS: undefined,
              conditionType: '',
              RHS: undefined,
              isValid: false,
            },
            ...condition,
            sourceGroupId: groupId,
            id: uuidv4(),
          }

          updateConditionGroupCount(group, 1)

          // If index is provided, insert at that position, otherwise append
          if (index !== undefined && index >= 0 && index <= group.conditions.length) {
            const updatedConditions = [...group.conditions]
            updatedConditions.splice(index, 0, newCondition)
            return {
              ...group,
              conditions: updatedConditions,
            }
          } else {
            return {
              ...group,
              conditions: group.conditions.concat(newCondition),
            }
          }
        }
      )

      return _set(updatedPattern, activeConditionGroupKey, updatedGroup)
    })
  }

  const handleChangeConditionGroup = (groupId: string, key: string, value: any) => {
    ReactGA.event('condition_group_change', {
      key: key,
      value: value ? value.toString() : '',
    })

    if (key === 'groupTitle') {
      // validate groupTitle does not contain and firebase invalid characters ".", "#", "$", "[", or "]"
      if (
        value.includes('.') ||
        value.includes('#') ||
        value.includes('$') ||
        value.includes('[') ||
        value.includes(']')
      ) {
        return
      }
    }

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]

      const updatedGroup = updateGroupInTree(
        activeConditionGroup,
        groupId,
        (group: ConditionGroupType) => {
          const newGroup: ConditionGroupType = _set(group, key, value)

          newGroup.conditions.forEach((condition) => {
            condition.chartDetails = {
              symbol: newGroup.symbol,
              periodicity: newGroup.periodicity,
              timeframe: newGroup.timeframe,
            }
          })

          return newGroup
        }
      )

      return _set(updatedPattern, activeConditionGroupKey, updatedGroup)
    })
  }

  const handleChangeCondition = (
    groupId: string,
    conditionId: string,
    updates: { [key: string]: any }
  ) => {
    ReactGA.event('condition_change', {
      keys: Object.keys(updates).join(','),
    })

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]

      const updatedGroup = updateConditionInGroup(
        activeConditionGroup,
        conditionId,
        (condition: ConditionType) => {
          // Create a copy of the condition
          const updatedCondition = _cloneDeep(condition)

          Object.entries(updates).forEach(([key, value]) => {
            // Handle special cases for LHS and RHS changes
            if (key === 'condition.LHS.indicator.name') {
              _set(updatedCondition.condition, 'LHS', undefined)

              const lhsIndicator = getIndicatorByValue?.(value)
              if (lhsIndicator && updatedCondition?.condition?.RHS?.indicator) {
                const supportedRHS = getRHS(lhsIndicator, condition?.condition?.LHS?.field)
                if (
                  updatedCondition.condition.RHS &&
                  supportedRHS.every(
                    (supported) =>
                      updatedCondition.condition.RHS?.indicator &&
                      supported.value !== updatedCondition.condition.RHS.indicator.name
                  )
                ) {
                  // if LHS indicator doesn't support RHS indicator, clear RHS
                  _set(updatedCondition.condition, 'RHS', undefined)
                }
              }
            } else if (key === 'condition.RHS.indicator.name') {
              _set(updatedCondition.condition, 'RHS', undefined)
            } else if (key === 'condition.conditionType') {
              const conditionType = getConditionTypes(updatedCondition.condition.LHS?.field)?.find(
                (c: any) => c.value === value
              )
              if (!conditionType || !conditionType.hasRHS) {
                // if condition type does not have RHS, clear RHS
                _set(updatedCondition.condition, 'RHS', undefined)
              }
            }

            _set(updatedCondition, key, value)
          })

          // Normalize + stamp condition.isValid (shared with the copilot's
          // draft normalization)
          normalizeConditionValidity(updatedCondition, getIndicatorByValue)

          return updatedCondition
        }
      )

      return _set(updatedPattern, activeConditionGroupKey, updatedGroup)
    })
  }

  const handleConditionDuplicate = (groupId: string, conditionId: string) => {
    ReactGA.event('condition_duplicate')

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]

      const { group: sourceGroup } = findGroupAndParent(activeConditionGroup, groupId)!
      const condition = findConditionInGroup(activeConditionGroup, conditionId)!

      const newCondition = {
        ..._cloneDeep(condition),
        id: uuidv4(),
      }

      const updatedGroup = updateGroupInTree(activeConditionGroup, sourceGroup.id, (group) => {
        updateConditionGroupCount(group, 1)
        return {
          ...group,
          conditions: group.conditions.concat(newCondition),
        }
      })

      return _set(updatedPattern, activeConditionGroupKey, updatedGroup)
    })
  }

  const handleConditionGroupDuplicate = (groupId: string) => {
    ReactGA.event('condition_group_duplicate')

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]

      const { parentGroup: sourceParentGroup, group } = findGroupAndParent(
        activeConditionGroup,
        groupId
      )!

      const newGroup = _cloneDeep(group)
      if (sourceParentGroup) {
        // If duplicating a nested group, use the parent's depth for hierarchy calculation
        recalculateGroupHierarchy(newGroup, sourceParentGroup.depth)
        // Make sure the sourceGroupId points to the parent
        newGroup.sourceGroupId = sourceParentGroup.id
      } else {
        // If duplicating a root level group
        recalculateGroupHierarchy(newGroup)
      }

      let updatedGroups
      if (sourceParentGroup) {
        updatedGroups = updateGroupInTree(activeConditionGroup, sourceParentGroup.id, (group) => {
          updateConditionGroupCount(group, 1)
          return {
            ...group,
            conditionGroups: group.conditionGroups.concat(newGroup),
          }
        })
      } else {
        updatedGroups = [...activeConditionGroup, newGroup]
      }

      return _set(updatedPattern, activeConditionGroupKey, updatedGroups)
    })
  }

  function handleChangePattern(key: string, value: any) {
    ReactGA.event('pattern_change', {
      key: key,
      value: value ? value.toString() : '',
    })

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      _set(updatedPattern, key, value)
      return updatedPattern
    })
  }

  const handleDrop = (groupId: string, item: any, dropIndex: number) => {
    // Skip if there's no patternId or if it doesn't match the current pattern
    if (!item.patternId || item.patternId !== pattern?.id) {
      return
    }

    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]

      // Special case for dropping to the root level
      if (groupId === 'ROOT_GROUP' && item.type === 'group') {
        // First, find the source group and its parent
        const sourceTabKey = prevPattern.buyConditionGroups.some(
          (g) => g.id === item.id || findConditionGroupInTree(g.conditionGroups, item.id)
        )
          ? 'buyConditionGroups'
          : 'sellConditionGroups'

        const sourceGroups = prevPattern[sourceTabKey]
        const { group: sourceGroup, parentGroup } = findGroupAndParent(sourceGroups, item.id) || {}

        if (!sourceGroup) return prevPattern

        // Create a new copy of the group for the root level
        const newRootGroup: ConditionGroupType = _cloneDeep(sourceGroup)
        recalculateGroupHierarchy(newRootGroup, 0, false) // Update depth only, preserve IDs

        // If it's in a different tab than the active one, just add it to the current tab
        if (sourceTabKey !== activeConditionGroupKey) {
          return _set(updatedPattern, activeConditionGroupKey, [
            ...activeConditionGroup,
            newRootGroup,
          ])
        }

        // If it's in the same tab, remove from original location and add to root
        let updatedGroups

        if (parentGroup) {
          // Remove from parent if it's a nested group
          updatedGroups = updateGroupInTree(activeConditionGroup, parentGroup.id, (group) => {
            // Properly decrement the count of the parent group when removing a nested group
            updateConditionGroupCount(group, -1)
            return {
              ...group,
              conditionGroups: group.conditionGroups.filter((g) => g.id !== item.id),
            }
          })
        } else {
          // If it's already at root level, just filter it out
          updatedGroups = activeConditionGroup.filter((g) => g.id !== item.id)
        }

        // Add the new group to the root
        return _set(updatedPattern, activeConditionGroupKey, [...updatedGroups, newRootGroup])
      }

      if (item.type === 'condition') {
        // Move condition
        // Add condition to the target group
        const { group: targetGroup } = findGroupAndParent(activeConditionGroup, groupId)!
        const { group: sourceGroup } = findGroupAndParent(activeConditionGroup, item.sourceGroupId)!

        // Check if dropIndex is valid, otherwise add to the end
        if (
          targetGroup.id !== item.sourceGroupId ||
          (dropIndex >= 0 && dropIndex <= targetGroup.conditions.length)
        ) {
          // Add condition to the target group
          let updatedGroups = updateGroupInTree(activeConditionGroup, targetGroup.id, (group) => {
            updateConditionGroupCount(group, 1)
            const condition = findConditionInGroup(activeConditionGroup, item.id)!
            condition.sourceGroupId = group.id
            condition.chartDetails = {
              symbol: group.symbol,
              periodicity: group.periodicity,
              timeframe: group.timeframe,
            }

            const existingConditionIndex = group.conditions.findIndex(
              (condition: any) => condition.id === item.id
            )
            if (existingConditionIndex > -1) {
              group.conditions.splice(existingConditionIndex, 1)
            }

            group.conditions.splice(dropIndex, 0, condition)

            return group
          })

          if (targetGroup.id !== item.sourceGroupId) {
            // remove from the source group
            updatedGroups = updateGroupInTree(updatedGroups, sourceGroup.id, (group) => {
              updateConditionGroupCount(group, -1)
              return {
                ...group,
                conditions: group.conditions.filter((condition: any) => condition.id !== item.id),
              }
            })
          }

          return _set(updatedPattern, activeConditionGroupKey, updatedGroups)
        }
      } else if (item.type === 'group') {
        // Move condition group
        if (groupId === item.sourceGroupId) return prevPattern // Skip if the source and target groups are the same
        if (groupId === item.id) return prevPattern // Skip if trying to drop a group into itself

        // Add the condition group to the target group
        const { group: targetGroup } = findGroupAndParent(activeConditionGroup, groupId)!
        const { group: movingGroup } = findGroupAndParent(activeConditionGroup, item.id)!

        let addedConditionGroupMaxDepth = findMaxDepth(movingGroup)
        if (targetGroup.depth + addedConditionGroupMaxDepth > MAX_CONDITION_GROUP_DEPTH) {
          return prevPattern
        }

        // Find and remove the condition group from the source group
        const { parentGroup: sourceParentGroup } = findGroupAndParent(
          activeConditionGroup,
          item.id
        )!

        if (sourceParentGroup) {
          // if not a root condition group, decrement the count of the parent group
          updateConditionGroupCount(sourceParentGroup, -1)
        }

        // increment the count of the target group
        updateConditionGroupCount(targetGroup, 1)

        let updatedGroups
        if (sourceParentGroup) {
          // If the group is not at the root level, update its parent
          const removedGroup = _cloneDeep(movingGroup)
          removedGroup.sourceGroupId = targetGroup.id

          // Update depth only, preserve IDs for moves
          recalculateGroupHierarchy(removedGroup, targetGroup.depth, false)

          updatedGroups = updateGroupInTree(activeConditionGroup, sourceParentGroup.id, (group) => {
            return {
              ...group,
              conditionGroups: group.conditionGroups.filter((g) => g.id !== item.id),
            }
          })

          updatedGroups = updateGroupInTree(updatedGroups, targetGroup.id, (group) => {
            group.conditionGroups.splice(dropIndex, 0, removedGroup)
            return group
          })
        } else {
          // If the group is at the root level
          const removedGroup = _cloneDeep(movingGroup)
          removedGroup.sourceGroupId = targetGroup.id

          // Update depth only, preserve IDs for moves
          recalculateGroupHierarchy(removedGroup, targetGroup.depth, false)

          updatedGroups = activeConditionGroup.filter((group) => group.id !== item.id)

          updatedGroups = updateGroupInTree(updatedGroups, targetGroup.id, (group) => {
            group.conditionGroups.splice(dropIndex, 0, removedGroup)
            return group
          })
        }

        return _set(updatedPattern, activeConditionGroupKey, updatedGroups)
      }

      return prevPattern
    })
  }

  const handleConditionGroupCopyTo = (groupId: string) => {
    setPattern((prevPattern) => {
      const updatedPattern = { ...prevPattern }
      const activeConditionGroupKey =
        activeTab === 'buy' ? 'buyConditionGroups' : 'sellConditionGroups'
      const inactiveConditionGroupKey =
        activeTab === 'buy' ? 'sellConditionGroups' : 'buyConditionGroups'
      const activeConditionGroup = prevPattern[activeConditionGroupKey]
      const inactiveConditionGroup = prevPattern[inactiveConditionGroupKey]

      const { group: sourceGroup } = findGroupAndParent(activeConditionGroup, groupId)!

      const newGroup = _cloneDeep(sourceGroup)
      // Recalculate as a root level group
      recalculateGroupHierarchy(newGroup)

      return _set(
        updatedPattern,
        inactiveConditionGroupKey,
        inactiveConditionGroup.concat(newGroup)
      )
    })
  }

  return (
    <PatternTreeContext.Provider
      value={{
        handleDrop,
        handleChangeCondition,
        handleChangeConditionGroup,
        handleAddCondition,
        handleRemoveCondition,
        handleRemoveConditionGroup,
        handleConditionDuplicate,
        addConditionGroup,
        handleAddNestedGroup,
        setActiveTab,
        activeTab,
        handleConditionGroupCopyTo,
        handleConditionGroupDuplicate,
        pattern,
        setPattern,
        handleChangePattern,
        savePattern,
        isSavingPattern,
        newlyCreatedConditionIds,
      }}
    >
      {children}
    </PatternTreeContext.Provider>
  )
}

interface patternModifers {
  handleDrop: (groupId: string, item: any, dropIndex: number) => void
  handleRemoveCondition: (groupId: string, conditionId: string) => void
  handleRemoveConditionGroup: (groupId: string) => void
  handleChangeCondition: (
    groupId: string,
    conditionId: string,
    updates: { [key: string]: any }
  ) => void
  handleChangeConditionGroup: (groupId: string, key: string, value: any) => void
  handleConditionDuplicate: (groupId: string, conditionId: string) => void
  handleAddCondition: (groupId: string, condition?: ConditionType, index?: number) => void
  addConditionGroup: (conditionGroup?: ConditionGroupType) => void
  handleAddNestedGroup: (groupId: string, conditionGroup?: ConditionGroupType) => void
  setActiveTab: (tab: 'buy' | 'sell') => void
  activeTab: string
  handleConditionGroupCopyTo: (groupId: string) => void
  handleConditionGroupDuplicate: (groupId: string) => void
  pattern: Pattern
  setPattern: (pattern: Pattern) => void
  handleChangePattern: (key: string, value: any) => void
  savePattern: (saveAsStandardPattern?: boolean) => Promise<Pattern | undefined>
  isSavingPattern: boolean
  newlyCreatedConditionIds: React.MutableRefObject<Set<string>>
}

export const PatternTreeContext = createContext<patternModifers>({} as patternModifers)
